import React, { useState, useEffect } from "react";
import { X, Save, Code } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/Textarea";
import { MarketingPopup } from "../../modules/shared/model/MarketingPopupInterfaces";
import LanguageDropdown from "../../components/ui/LanguageDropdown";
import { Language } from "../../modules/shared/model/LanguageInterfaces";

interface MarketingPopupModalProps {
  isOpen: boolean;
  onClose: () => void;
  popup?: MarketingPopup | null;
  onSave: (data: Partial<MarketingPopup>) => void;
  mode: "create" | "edit";
}

const MarketingPopupModal: React.FC<MarketingPopupModalProps> = ({
  isOpen,
  onClose,
  popup,
  onSave,
  mode,
}) => {
  const [formData, setFormData] = useState<Partial<MarketingPopup>>({
    name: "",
    status: "draft",
    contentTitle: "",
    contentDescription: "",
    contentPrimaryButtonText: "",
    contentPrimaryButtonLink: "",
    contentSecondaryButtonText: "",
    contentSecondaryButtonLink: "",
    timingStartDate: "",
    timingEndDate: "",
    timingDelay: "",
    designWidth: "500px",
    designHeight: "auto",
    designColorsBackground: "#FFFFFF",
    designColorsText: "#1F2937",
    designColorsPrimary: "#2563EB",
    behaviorShowCloseButton: true,
    behaviorCloseOnEscape: true,
    behaviorCloseOnOverlayClick: true,
    targetingUserType: "",
    targetingDevices: [],
    designAnimation: { id: "1" },
    timingDisplayFrequency: { id: "1" },
    displayLocation: { id: "1" },
    pageLocation: { id: "1" },
    categoryType: { id: "1" },
    designTheme: { id: "1" },
    targetingAudience: { id: "1" },
    type: { id: "1" },
    timingShowTimeStart: "",
    timingShowTimeEnd: "",
    designColorsSecondary: "#6B7280",
    designCornerRadius: "8px",
    designOverlay: true,
    designTypographyTitleSize: "24px",
    designTypographyDescriptionSize: "16px",
    designTypographyFontFamily: "Inter, sans-serif",
    designSpacingPadding: "24px",
    designSpacingGap: "16px",
    behaviorAutoClose: "",
    behaviorScrollBehavior: "allow",
    contentVideoUrl: "",
    contentPrimaryButtonStyle: "primary",
    contentSecondaryButtonStyle: "outline",
    targetingLocations: "all",
    targetingLanguage: [],
    targetingUrlPatterns: ["/*"],
    performanceImpressions: 0,
    performanceClicks: 0,
    performanceDismissals: 0,
    performanceConversionRate: "0%",
    performanceAvgViewTime: "0s"
  });

  const [activeTab, setActiveTab] = useState("content");
  const [contentType, setContentType] = useState<"text" | "html">("text");
  const [selectedLanguages, setSelectedLanguages] = useState<Language[]>([]);
  const [urlPatternsText, setUrlPatternsText] = useState<string>("/*");

  useEffect(() => {
    if (popup && mode === "edit") {
      setFormData(popup);
      // Detect if description contains HTML
      if (popup.contentDescription && /^\s*<[^>]+>/.test(popup.contentDescription)) {
        setContentType("html");
      }
      // Load existing languages
      if (popup.targetingLanguage && Array.isArray(popup.targetingLanguage)) {
        setSelectedLanguages(popup.targetingLanguage as Language[]);
      }
      // Load existing URL patterns
      if (popup.targetingUrlPatterns && popup.targetingUrlPatterns.length > 0) {
        setUrlPatternsText(popup.targetingUrlPatterns.join("\n"));
      }
    }
  }, [popup, mode]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Clean up the form data before saving
    const cleanedData = { ...formData };
    
    // Convert selected languages to the format expected by backend
    if (selectedLanguages && selectedLanguages.length > 0) {
      cleanedData.targetingLanguage = selectedLanguages.map(lang => ({ id: lang.id }));
    } else {
      cleanedData.targetingLanguage = [];
    }
    
    // Convert URL patterns text to array
    if (urlPatternsText && urlPatternsText.trim()) {
      cleanedData.targetingUrlPatterns = urlPatternsText
        .split("\n")
        .map(pattern => pattern.trim())
        .filter(pattern => pattern.length > 0);
    } else {
      cleanedData.targetingUrlPatterns = ["/*"];
    }
    
    onSave(cleanedData);
    onClose();
  };

  // Handle language selection
  const handleLanguagesSelect = (languages: Language[]) => {
    setSelectedLanguages(languages);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold">
            {mode === "create" ? "Create New Popup" : "Edit Popup"}
          </h2>
          <Button variant="outline" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b flex-shrink-0">
          <div className="flex space-x-8 px-6">
            {[
              { id: "content", label: "Content" },
              { id: "display", label: "Display Settings" },
              { id: "targeting", label: "Targeting" },
              { id: "design", label: "Design" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {activeTab === "content" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Popup Name
                  </label>
                  <Input
                    value={formData.name || ""}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Enter popup name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status || "draft"}
                    onChange={(e) => handleInputChange("status", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="paused">Paused</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Notifications will be sent to all target users
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type
                  </label>
                  <select
                    value={formData.type?.id || "1"}
                    onChange={(e) => handleInputChange("type", { id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1">Welcome</option>
                    <option value="2">Promotional</option>
                    <option value="3">Newsletter</option>
                    <option value="4">Exit Intent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={formData.categoryType?.id || "1"}
                    onChange={(e) => handleInputChange("categoryType", { id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1">General</option>
                    <option value="2">Professional</option>
                    <option value="3">Client</option>
                    <option value="4">Emergency</option>
                  </select>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Content</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title
                    </label>
                    <Input
                      value={formData.contentTitle || ""}
                      onChange={(e) => handleInputChange("contentTitle", e.target.value)}
                      placeholder="Enter title"
                    />
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <div className="flex rounded-md overflow-hidden border">
                        <button
                          type="button"
                          className={`px-3 py-1 text-xs ${contentType === "text" ? "bg-blue-100 text-blue-700" : "bg-gray-100"}`}
                          onClick={() => setContentType("text")}
                        >
                          Text
                        </button>
                        <button
                          type="button"
                          className={`px-3 py-1 text-xs ${contentType === "html" ? "bg-blue-100 text-blue-700" : "bg-gray-100"}`}
                          onClick={() => setContentType("html")}
                        >
                          HTML
                        </button>
                      </div>
                    </div>
                    <div className="flex items-start gap-2">
                      {contentType === "html" && <Code className="h-5 w-5 text-gray-400 mt-2" />}
                      <Textarea
                        value={formData.contentDescription || ""}
                        onChange={(e) => handleInputChange("contentDescription", e.target.value)}
                        placeholder={contentType === "html" ? "Enter HTML content" : "Enter description"}
                        rows={4}
                        className={contentType === "html" ? "font-mono" : ""}
                      />
                    </div>
                    {contentType === "html" && (
                      <p className="text-xs text-gray-500 mt-1">You can use HTML tags to format your content.</p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Primary Button</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Button text
                    </label>
                    <Input
                      value={formData.contentPrimaryButtonText || ""}
                      onChange={(e) => handleInputChange("contentPrimaryButtonText", e.target.value)}
                      placeholder="Button text"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Button link
                    </label>
                    <Input
                      value={formData.contentPrimaryButtonLink || ""}
                      onChange={(e) => handleInputChange("contentPrimaryButtonLink", e.target.value)}
                      placeholder="Button link"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Secondary Button</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Button text
                    </label>
                    <Input
                      value={formData.contentSecondaryButtonText || ""}
                      onChange={(e) => handleInputChange("contentSecondaryButtonText", e.target.value)}
                      placeholder="Button text"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Button link
                    </label>
                    <Input
                      value={formData.contentSecondaryButtonLink || ""}
                      onChange={(e) => handleInputChange("contentSecondaryButtonLink", e.target.value)}
                      placeholder="Button link"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "display" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Display Location
                  </label>
                  <select
                    value={formData.displayLocation?.id || "1"}
                    onChange={(e) => handleInputChange("displayLocation", { id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1">Center</option>
                    <option value="2">Top</option>
                    <option value="3">Bottom</option>
                    <option value="4">Left</option>
                    <option value="5">Right</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Display Frequency
                  </label>
                  <select
                    value={formData.timingDisplayFrequency?.id || "1"}
                    onChange={(e) => handleInputChange("timingDisplayFrequency", { id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1">Once</option>
                    <option value="2">Once per session</option>
                    <option value="3">Every visit</option>
                    <option value="4">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Display Delay (seconds)
                  </label>
                  <Input
                    type="number"
                    value={formData.timingDelay || ""}
                    onChange={(e) => handleInputChange("timingDelay", e.target.value)}
                    placeholder="3"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date (Optional)
                  </label>
                  <Input
                    type="datetime-local"
                    value={formData.timingStartDate || ""}
                    onChange={(e) => handleInputChange("timingStartDate", e.target.value)}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Leave empty to send immediately
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date (Optional)
                  </label>
                  <Input
                    type="datetime-local"
                    value={formData.timingEndDate || ""}
                    onChange={(e) => handleInputChange("timingEndDate", e.target.value)}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Leave empty for no expiration
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Show Time Start
                  </label>
                  <Input
                    type="time"
                    value={formData.timingShowTimeStart || "00:00"}
                    onChange={(e) => handleInputChange("timingShowTimeStart", e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Show Time End
                  </label>
                  <Input
                    type="time"
                    value={formData.timingShowTimeEnd || "23:59"}
                    onChange={(e) => handleInputChange("timingShowTimeEnd", e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === "targeting" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Target Audience
                  </label>
                  <select
                    value={formData.targetingAudience?.id || "1"}
                    onChange={(e) => handleInputChange("targetingAudience", { id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1">All Users</option>
                    <option value="2">New Visitors</option>
                    <option value="3">Returning Users</option>
                    <option value="4">Professionals</option>
                    <option value="5">Clients</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User Type
                  </label>
                  <select
                    value={formData.targetingUserType || ""}
                    onChange={(e) => handleInputChange("targetingUserType", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All Users</option>
                    <option value="guest">Guest Users</option>
                    <option value="registered">Registered Users</option>
                    <option value="professional">Professionals</option>
                    <option value="client">Clients</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Devices
                </label>
                <div className="flex gap-4">
                  {["desktop", "mobile", "tablet"].map((device) => (
                    <label key={device} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.targetingDevices?.includes(device) || false}
                        onChange={(e) => {
                          const devices = formData.targetingDevices || [];
                          if (e.target.checked) {
                            handleInputChange("targetingDevices", [...devices, device]);
                          } else {
                            handleInputChange("targetingDevices", devices.filter(d => d !== device));
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm capitalize">{device}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Languages
                </label>
                <LanguageDropdown
                  multiple={true}
                  selectedLanguages={selectedLanguages}
                  onLanguageSelect={() => {}}
                  onLanguagesSelect={handleLanguagesSelect}
                  placeholder="Select languages (optional)"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Leave empty to target all languages. Select specific languages to limit popup display.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL Patterns
                </label>
                <Textarea
                  value={urlPatternsText}
                  onChange={(e) => setUrlPatternsText(e.target.value)}
                  placeholder="Enter URL patterns (one per line)&#10;Examples:&#10;/*  (all pages)&#10;/professional*  (professional pages)&#10;/services/*  (all service pages)"
                  rows={5}
                  className="font-mono text-sm"
                />
              </div>
            </div>
          )}

          {activeTab === "design" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Width
                  </label>
                  <Input
                    value={formData.designWidth || ""}
                    onChange={(e) => handleInputChange("designWidth", e.target.value)}
                    placeholder="500px"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Height
                  </label>
                  <Input
                    value={formData.designHeight || ""}
                    onChange={(e) => handleInputChange("designHeight", e.target.value)}
                    placeholder="auto"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Color
                  </label>
                  <Input
                    type="color"
                    value={formData.designColorsBackground || "#FFFFFF"}
                    onChange={(e) => handleInputChange("designColorsBackground", e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Text Color
                  </label>
                  <Input
                    type="color"
                    value={formData.designColorsText || "#1F2937"}
                    onChange={(e) => handleInputChange("designColorsText", e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Color
                  </label>
                  <Input
                    type="color"
                    value={formData.designColorsPrimary || "#2563EB"}
                    onChange={(e) => handleInputChange("designColorsPrimary", e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Secondary Color
                  </label>
                  <Input
                    type="color"
                    value={formData.designColorsSecondary || "#6B7280"}
                    onChange={(e) => handleInputChange("designColorsSecondary", e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Design Theme
                  </label>
                  <select
                    value={formData.designTheme?.id || "1"}
                    onChange={(e) => handleInputChange("designTheme", { id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1">Light</option>
                    <option value="2">Dark</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Animation
                  </label>
                  <select
                    value={formData.designAnimation?.id || "1"}
                    onChange={(e) => handleInputChange("designAnimation", { id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1">None</option>
                    <option value="2">Fade</option>
                    <option value="3">Slide</option>
                    <option value="4">Zoom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Page Location
                  </label>
                  <select
                    value={formData.pageLocation?.id || "1"}
                    onChange={(e) => handleInputChange("pageLocation", { id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1">All Pages</option>
                    <option value="2">Homepage</option>
                    <option value="3">Professional Page</option>
                    <option value="4">International Professionals</option>
                    <option value="5">Services Pages</option>
                    <option value="6">Blog Pages</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Behavior Settings
                </label>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.behaviorShowCloseButton || false}
                      onChange={(e) => handleInputChange("behaviorShowCloseButton", e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">Show Close Button</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.behaviorCloseOnEscape || false}
                      onChange={(e) => handleInputChange("behaviorCloseOnEscape", e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">Close on Escape Key</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.behaviorCloseOnOverlayClick || false}
                      onChange={(e) => handleInputChange("behaviorCloseOnOverlayClick", e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">Close on Overlay Click</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.designOverlay || false}
                      onChange={(e) => handleInputChange("designOverlay", e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">Show Overlay</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50 flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Save className="h-4 w-4 mr-2" />
            {mode === "create" ? "Create" : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MarketingPopupModal;
